/* IDEAL: optional offer before the customer's original checkout navigation. */
(function (global) {
  'use strict';

  const STORAGE_KEY = 'ideal:checkout-upsell:decisions:v1';
  const MARKER = '_ideal_checkout_upsell';
  const SKIP_SELECTOR = '.dynamic-cart-btns, shopify-accelerated-checkout, .shopify-payment-button, [data-shopify-buttoncontainer], [data-ideal-upsell-skip]';

  function availableVariants(offer) {
    return (Array.isArray(offer.variants) ? offer.variants : []).filter((variant) =>
      variant.available === true && Number(variant.id) > 0 && Number.isFinite(Number(variant.price)) && Number(variant.price) >= 0);
  }

  function chooseOffer(data) {
    if (!data || data.enabled !== true || !Array.isArray(data.offers)) return null;
    const products = (data.cartProductIds || []).map(String);
    return data.offers.map((offer, index) => ({ offer, index }))
      .filter(({ offer }) => products.includes(String(offer.sourceProductId)) &&
        !products.includes(String(offer.productId)) && availableVariants(offer).length)
      .sort((a, b) => (Number(b.offer.priority) || 0) - (Number(a.offer.priority) || 0) || a.index - b.index)
      .map(({ offer }) => offer)[0] || null;
  }

  function isCheckoutURL(href, origin) {
    try {
      const url = new URL(href, origin);
      return url.origin === new URL(origin).origin && /\/checkout\/?$/.test(url.pathname);
    } catch (_) { return false; }
  }

  function isCartURL(href, origin) {
    try {
      const url = new URL(href, origin);
      return url.origin === new URL(origin).origin && /\/cart\/?$/.test(url.pathname);
    } catch (_) { return false; }
  }

  function hasAttempt(cart, attempt) {
    return !!cart && Array.isArray(cart.items) && cart.items.some((item) =>
      item.properties && item.properties[MARKER] === attempt && Number(item.quantity) > 0);
  }

  function cartCodes(cart, extra) {
    return [...new Set((cart.discount_codes || []).map((item) => typeof item === 'string' ? item : item.code)
      .concat(extra || []).filter(Boolean))];
  }

  function quoteItems(cart) {
    return cart.items.map((item) => ({
      id: item.variant_id, quantity: item.quantity,
      ...(item.properties && Object.keys(item.properties).length ? { properties: item.properties } : {}),
      ...(item.selling_plan_allocation ? { selling_plan: item.selling_plan_allocation.selling_plan.id } : {})
    }));
  }

  function validateQuote(value, currency, needsDiscount) {
    if (!value || value.available !== true || !Number.isInteger(value.price) || value.price < 0 ||
      !Number.isInteger(value.originalPrice) || value.originalPrice < value.price ||
      !Number.isInteger(value.totalPrice) || value.totalPrice < value.price ||
      (currency && value.currency !== currency) || (needsDiscount && value.price >= value.originalPrice)) {
      throw new Error('Ponuda s popustom trenutno nije dostupna.');
    }
    return value;
  }

  function decisionStore(storage) {
    // Browsers in private mode may reject sessionStorage. Keep the same semantics in memory.
    let memory = [];
    try { memory = JSON.parse(storage.getItem(STORAGE_KEY) || '[]'); } catch (_) { /* optional */ }
    if (!Array.isArray(memory)) memory = [];
    memory = memory.filter((entry) => typeof entry === 'string');
    return {
      has(signature) { return !!signature && memory.includes(String(signature)); },
      add(signature) {
        if (!signature) return;
        memory = memory.filter((entry) => entry !== String(signature));
        memory.push(String(signature));
        memory = memory.slice(-40);
        try { storage.setItem(STORAGE_KEY, JSON.stringify(memory)); } catch (_) { /* optional */ }
      }
    };
  }

  function collectCartFields(entries) {
    const body = {};
    const attributes = {};
    entries.forEach(([name, value]) => {
      if (typeof value !== 'string') return;
      if (name === 'note') body.note = value;
      const attribute = /^attributes\[([^\]]+)\]$/.exec(name);
      if (attribute) attributes[attribute[1]] = value;
    });
    if (Object.keys(attributes).length) body.attributes = attributes;
    return body;
  }

  function quantityUpdates(quantities, cart) {
    const updates = {};
    quantities.forEach((quantity) => {
      if (!quantity.dirty) return;
      const value = Number(quantity.value);
      if (!Number.isInteger(value) || value < 0) throw new Error('Invalid cart quantity');
      let item = cart.items[Number(quantity.index) - 1];
      if (!item || String(item.variant_id) !== String(quantity.variantId)) {
        const matches = cart.items.filter((line) => String(line.variant_id) === String(quantity.variantId));
        if (matches.length !== 1) throw new Error('Cart changed while saving quantities');
        [item] = matches;
      }
      if (value !== item.quantity) updates[item.key] = value;
    });
    return updates;
  }

  function createController(win, doc) {
    const dialog = doc.getElementById('ideal-checkout-upsell');
    const initial = doc.getElementById('ideal-checkout-upsell-data');
    if (!dialog || !initial || typeof dialog.showModal !== 'function') return null;
    let settings;
    try { settings = JSON.parse(initial.textContent); } catch (_) { return null; }
    if (!settings.enabled) return null;

    const root = (win.Shopify && win.Shopify.routes && win.Shopify.routes.root) ||
      (win.routes && win.routes.root) || '/';
    const route = (path) => root.replace(/\/?$/, '/') + path;
    let storage;
    try { storage = win.sessionStorage; } catch (_) { storage = null; }
    const decisions = decisionStore(storage);
    const el = {};
    ['title', 'heading', 'description', 'image', 'video', 'badge', 'compare', 'price', 'variant', 'accept', 'decline',
      'close', 'error', 'status', 'summary', 'caption', 'variant-wrap'].forEach((name) => {
      el[name] = dialog.querySelector('[data-upsell-' + name + ']');
    });
    if (!el.accept || !el.decline || !el.variant) return null;
    const acceptLabel = el.accept.textContent;
    const declineLabel = el.decline.textContent;
    let busy = false;
    let adding = false;
    let navigating = false;
    let active = null;
    let bypassForm = null;
    let ambiguousAttempt = null;
    let didAdd = false;
    let quoting = false;
    let quoteSequence = 0;
    let quoted = null;
    let rollbackPending = false;
    let pendingQuote = null;
    let verifiedCodes = [];
    let generation = 0;
    function attemptId() {
      if (win.crypto && typeof win.crypto.randomUUID === 'function') return win.crypto.randomUUID();
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
        const value = Math.floor(Math.random() * 16);
        return (char === 'x' ? value : (value & 3) | 8).toString(16);
      });
    }

    async function request(path, options, timeout) {
      const controller = new win.AbortController();
      const timer = win.setTimeout(() => controller.abort(), timeout || 8000);
      try {
        return await win.fetch(route(path), {
          credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal
        });
      } finally { win.clearTimeout(timer); }
    }

    async function readCart() {
      const response = await request('cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Cart unavailable');
      const cart = await response.json();
      if (!Array.isArray(cart.items)) throw new Error('Invalid cart');
      return cart;
    }

    async function readOffers() {
      const response = await request('cart?section_id=ideal-checkout-upsell', { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Offer unavailable');
      const parsed = new win.DOMParser().parseFromString(await response.text(), 'text/html');
      const data = parsed.getElementById('ideal-checkout-upsell-data');
      if (!data) throw new Error('Offer unavailable');
      const value = JSON.parse(data.textContent);
      if (!Array.isArray(value.cartProductIds) || !value.cartSignature || !Array.isArray(value.offers)) {
        throw new Error('Invalid offer');
      }
      return value;
    }

    function text(node, value) { if (node) node.textContent = value || ''; }
    function money(value) {
      try {
        return new Intl.NumberFormat(doc.documentElement.lang || 'bs-BA', {
          style: 'currency', currency: active.data.currency || settings.currency || 'BAM', currencyDisplay: 'narrowSymbol'
        }).format(Number(value) / 100);
      } catch (_) { return (Number(value) / 100).toFixed(2) + ' ' + (settings.currency || 'BAM'); }
    }
    function message(value) {
      text(el.error, value);
      if (el.error) el.error.hidden = !value;
    }
    function setAdding(value) {
      adding = value;
      dialog.setAttribute('aria-busy', String(value));
      el.accept.disabled = value || quoting || rollbackPending;
      el.variant.disabled = value;
      el.decline.disabled = value;
      if (el.close) el.close.disabled = value;
      el.accept.textContent = value ? 'Provjeravamo ponudu…' : ambiguousAttempt ? 'Provjeri dodavanje' : acceptLabel;
    }

    function renderVariant() {
      const variant = availableVariants(active.offer).find((item) => String(item.id) === el.variant.value);
      if (!variant) return;
      text(el.price, money(variant.price));
      const compare = Number(variant.compareAtPrice);
      text(el.compare, compare > Number(variant.price) ? money(compare) : '');
      if (el.compare) el.compare.hidden = !(compare > Number(variant.price));
      const media = active.offer.media || {};
      const video = media.type === 'video' && media.src && el.video;
      if (el.video) {
        el.video.hidden = !video;
        if (typeof el.video.pause === 'function') el.video.pause();
        if (video && el.video.getAttribute('src') !== media.src) {
          el.video.src = media.src;
          if (media.poster) el.video.poster = media.poster;
        }
      }
      const src = media.type === 'image' && media.src || variant.image || active.offer.image;
      if (el.image) {
        el.image.hidden = !!video || !src;
        if (src) el.image.src = src;
        el.image.alt = active.offer.imageAlt || active.offer.title || '';
      }
      text(el.summary, 'Ukupno s dodatkom: ' + money(Number(active.data.cartTotal) + Number(variant.price)));
    }

    async function quoteOffer(offer, variant, cart) {
      if (!offer.discountCode) return {
        available: true, price: Number(variant.price), originalPrice: Number(variant.price),
        totalPrice: Number(cart.total_price) + Number(variant.price), currency: active.data.currency
      };
      const endpoint = active.data.quoteEndpoint || settings.quoteEndpoint;
      if (!endpoint || !/^https:\/\//.test(endpoint)) throw new Error('Provjera ponude trenutno nije dostupna.');
      const controller = new win.AbortController();
      const timer = win.setTimeout(() => controller.abort(), 15000);
      try {
        const response = await win.fetch(endpoint, {
          method: 'POST', mode: 'cors', credentials: 'omit', cache: 'no-store', signal: controller.signal,
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: quoteItems(cart), discountCodes: cartCodes(cart, offer.discountCode),
            offerVariantId: Number(variant.id), sourceProductId: Number(offer.sourceProductId),
            offerDiscountCode: offer.discountCode, offerAttemptId: active.attempt, currency: active.data.currency
          })
        });
        if (!response.ok) throw new Error('Provjera ponude trenutno nije dostupna.');
        return validateQuote(await response.json(), active.data.currency, true);
      } finally { win.clearTimeout(timer); }
    }

    async function refreshQuote() {
      const sequence = ++quoteSequence;
      quoted = null;
      quoting = true;
      text(el.caption, 'Po specijalnoj cijeni uz vašu narudžbu.');
      if (el.badge) el.badge.hidden = true;
      el.accept.disabled = true;
      text(el.status, 'Provjeravamo cijenu ponude…');
      try {
        const variant = availableVariants(active.offer).find((item) => String(item.id) === el.variant.value);
        if (!variant) throw new Error('Varijanta nije dostupna.');
        const result = await quoteOffer(active.offer, variant, await readCart());
        if (!active || sequence !== quoteSequence) return false;
        quoted = result;
        text(el.price, money(result.price));
        const compare = result.originalPrice > result.price ? result.originalPrice : Number(variant.compareAtPrice);
        text(el.compare, compare > result.price ? money(compare) : '');
        if (el.compare) el.compare.hidden = !(compare > result.price);
        text(el.summary, 'Ukupno s dodatkom: ' + money(result.totalPrice));
        const discountPercent = Number(result.discountPercent);
        const hasDiscount = Number.isFinite(discountPercent) && discountPercent > 0;
        text(el.badge, hasDiscount ? '-' + discountPercent + '%' : '');
        if (el.badge) el.badge.hidden = !hasDiscount;
        text(el.caption, 'Po specijalnoj cijeni uz vašu narudžbu' +
          (hasDiscount ? ' — ' + discountPercent + '% popusta.' : '.'));
        text(el.status, '');
        return true;
      } catch (error) {
        if (active && sequence === quoteSequence) message(error.message || 'Provjera ponude nije uspjela.');
        return false;
      } finally {
        if (sequence === quoteSequence) {
          quoting = false;
          el.accept.disabled = adding || !quoted || rollbackPending;
        }
      }
    }

    function render(offer, data, keepVariant) {
      active.offer = offer;
      active.data = data;
      text(el.title, offer.title);
      if (offer.heading) text(el.heading, offer.heading);
      text(el.description, offer.description);
      if (el.description) el.description.hidden = !offer.description;
      el.variant.replaceChildren();
      const variants = availableVariants(offer);
      variants.forEach((variant) => {
        const option = doc.createElement('option');
        option.value = String(variant.id);
        option.textContent = variant.title === 'Default Title' ? offer.title : variant.title;
        el.variant.appendChild(option);
      });
      const wanted = keepVariant || String(offer.defaultVariantId);
      el.variant.value = variants.some((variant) => String(variant.id) === wanted) ? wanted : String(variants[0].id);
      if (el['variant-wrap']) el['variant-wrap'].hidden = variants.length < 2;
      renderVariant();
    }

    function associatedForm(link) {
      const nearest = link.closest('form') || link.closest('cart-drawer, .cc-main-cart');
      return nearest && (nearest.tagName === 'FORM' ? nearest : nearest.querySelector('form'));
    }

    function valid(form, report) {
      if (!form) return true;
      if (!form.checkValidity()) {
        if (report) form.reportValidity();
        return false;
      }
      const unchecked = Array.from(form.elements).find((field) =>
        field.matches && field.matches('.js-cart-terms-checkbox') && !field.checked);
      if (!unchecked) return true;
      if (report) {
        unchecked.focus();
        win.alert((win.theme && win.theme.strings.cartTermsConfirmation) || 'Prihvatite uslove kupovine prije nastavka.');
      }
      return false;
    }

    function snapshot(form, submitter, href) {
      const fields = form ? Array.from(new win.FormData(form).entries()) : [];
      const quantities = form ? Array.from(form.elements).filter((field) => field.name === 'updates[]' && !field.disabled)
        .map((field) => ({
          value: field.value, index: field.dataset.index,
          variantId: field.dataset.variantId || (field.closest('.cart-item') || { dataset: {} }).dataset.variantId,
          dirty: String(field.value) !== String(field.dataset.initialValue)
        })) : [];
      return {
        form, formId: form && form.id, submitter, href, fields, quantities,
        submitterName: submitter && submitter.name || 'checkout',
        submitterValue: submitter && submitter.value || '',
        submitterOverrides: submitter ? ['formaction', 'formmethod', 'formenctype', 'formtarget', 'formnovalidate']
          .filter((name) => submitter.hasAttribute(name)).map((name) => [name, submitter.getAttribute(name)]) : []
      };
    }

    async function savePending(intent) {
      if (!intent.form) return;
      // The theme debounces quantity changes. Let that update finish before applying saved edits.
      if (intent.quantities.some((quantity) => quantity.dirty)) {
        await new Promise((resolve) => win.setTimeout(resolve, 350));
      }
      const started = Date.now();
      while (doc.querySelector('cart-items.pointer-events-none')) {
        if (Date.now() - started > 5000) throw new Error('Cart update still running');
        await new Promise((resolve) => win.setTimeout(resolve, 80));
      }
      const body = collectCartFields(intent.fields);
      if (intent.quantities.some((quantity) => quantity.dirty)) {
        const updates = quantityUpdates(intent.quantities, await readCart());
        if (Object.keys(updates).length) body.updates = updates;
      }
      if (!Object.keys(body).length) return;
      const response = await request('cart/update.js', {
        method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify(body)
      });
      if (!response.ok) throw new Error('Cart edits could not be saved');
      await response.json();
    }

    function resume(intent) {
      if (navigating) return;
      const form = intent.formId && doc.getElementById(intent.formId) || intent.form;
      if (!valid(form, true)) {
        if (dialog.open) dialog.close();
        active = null;
        busy = false;
        return;
      }
      navigating = true;
      busy = true;
      if (dialog.open) dialog.close();
      if (el.video && typeof el.video.pause === 'function') el.video.pause();
      if (intent.href) {
        const destination = new URL(intent.href, win.location.origin);
        if (didAdd && verifiedCodes.length && destination.searchParams.has('discount')) {
          destination.searchParams.set('discount', verifiedCodes.join(','));
        }
        win.location.assign(destination.href);
        return;
      }
      if (!form || !form.isConnected) {
        win.location.assign(route('checkout'));
        return;
      }
      // The keyed cart edits are already saved. Old positional updates[] inputs may
      // describe a different order after an add, another tab, or browser Back.
      const disabled = [];
      const restoredValues = [];
      if (didAdd || intent.saved) Array.from(form.elements).forEach((field) => {
        if (field.name === 'updates[]' && !field.disabled) {
          field.disabled = true;
          disabled.push(field);
        }
        if (field.name === 'discount' && verifiedCodes.length && !field.disabled) {
          restoredValues.push([field, field.value]);
          field.value = verifiedCodes.join(',');
        }
      });
      let button = intent.submitter;
      let synthetic = false;
      if (!button || !button.isConnected || button.form !== form) {
        button = doc.createElement('button');
        button.type = 'submit';
        button.hidden = true;
        button.name = intent.submitterName;
        button.value = intent.submitterValue;
        intent.submitterOverrides.forEach(([name, value]) => button.setAttribute(name, value));
        form.appendChild(button);
        synthetic = true;
      }
      bypassForm = form;
      try {
        form.requestSubmit(button);
      } finally {
        bypassForm = null;
        if (synthetic) button.remove();
        disabled.forEach((field) => { field.disabled = false; });
        restoredValues.forEach(([field, value]) => { field.value = value; });
        // Another legitimate submit listener may cancel navigation (e.g. validation).
        navigating = false;
        busy = false;
        active = null;
      }
    }

    async function start(intent) {
      const operation = ++generation;
      busy = true;
      didAdd = false;
      ambiguousAttempt = null;
      rollbackPending = false;
      pendingQuote = null;
      verifiedCodes = [];
      active = { intent, attempt: attemptId() };
      el.decline.textContent = declineLabel;
      try {
        await savePending(intent);
        if (operation !== generation) return;
        intent.saved = true;
        const data = await readOffers();
        if (operation !== generation) return;
        const offer = chooseOffer(data);
        if (!offer || decisions.has(data.cartSignature)) {
          resume(intent);
          return;
        }
        render(offer, data);
        message('');
        text(el.status, '');
        setAdding(false);
        const ready = await refreshQuote();
        if (operation !== generation) return;
        if (!ready) { resume(intent); return; }
        // The drawer has a separate focus trap. Close it before opening the native dialog.
        const drawer = doc.getElementById('cart-drawer');
        if (drawer && drawer.getAttribute('aria-hidden') === 'false' && typeof drawer.close === 'function') drawer.close();
        dialog.showModal();
        el.accept.focus();
      } catch (_) {
        if (operation === generation) resume(intent); // Optional offer must never prevent checkout on a read/network failure.
      }
    }

    function decline() {
      if (!active || adding || navigating) return;
      if (rollbackPending || ambiguousAttempt) {
        message('Nismo mogli potvrditi uklanjanje ponude. Otvorite korpu i provjerite stavke prije plaćanja.');
        win.location.assign(route('cart'));
        return;
      }
      decisions.add(active.data && active.data.cartSignature);
      resume(active.intent);
    }

    async function completeAddition() {
      didAdd = true;
      decisions.add(active.data.cartSignature);
      text(el.status, 'Proizvod je dodan. Nastavljamo na plaćanje…');
      // Remember the cart AFTER adding too, so browser Back does not offer another add-on.
      try { decisions.add((await readOffers()).cartSignature); } catch (_) { /* Original fingerprint is already saved. */ }
      const intent = active.intent;
      ambiguousAttempt = null;
      setAdding(false);
      resume(intent);
    }

    async function finalizeDiscount(attempt, expected) {
      // Applying a native Shopify code is a separate mutation; verify it before navigation.
      try {
        let cart = await readCart();
        if (active.offer.discountCode) {
          const response = await request('cart/update.js', {
            method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({ discount: cartCodes(cart, active.offer.discountCode).join(',') })
          });
          if (!response.ok) throw new Error('Discount update failed');
          cart = await response.json();
        }
        const line = cart.items.find((item) => item.properties && item.properties[MARKER] === attempt);
        if (!line || Number(line.quantity) !== 1 || Number(line.final_line_price) !== expected.price ||
          Number(cart.total_price) !== expected.totalPrice) {
          throw new Error('Discount did not match quote');
        }
        verifiedCodes = cartCodes(cart);
        await completeAddition();
        return;
      } catch (_) { /* Roll back only our added line, never another customer item. */ }
      try {
        const cart = await readCart();
        const line = cart.items.find((item) => item.properties && item.properties[MARKER] === attempt);
        if (line) {
          const response = await request('cart/change.js', {
            method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: 0 })
          });
          if (!response.ok || hasAttempt(await response.json(), attempt)) throw new Error('Rollback failed');
        }
        ambiguousAttempt = null;
        pendingQuote = null;
        active.attempt = attemptId();
        message('Popust nije potvrđen. Proizvod nije dodan u narudžbu. Možete pokušati ponovo ili nastaviti bez ponude.');
        text(el.status, '');
      } catch (_) {
        rollbackPending = true;
        message('Nismo mogli potvrditi popust ni uklanjanje ponude. Otvorite korpu i provjerite stavke prije plaćanja.');
        el.decline.textContent = 'Otvori korpu';
      }
      if (active) setAdding(false);
    }

    async function verifyAmbiguous() {
      try {
        if (hasAttempt(await readCart(), ambiguousAttempt)) {
          await finalizeDiscount(ambiguousAttempt, pendingQuote);
          return true;
        }
      } catch (_) { /* Preserve the uncertain state. Never blindly POST again. */ }
      message('Dodavanje još nije potvrđeno. Kliknite „Provjeri dodavanje” ili otvorite korpu da provjerite stavke prije plaćanja.');
      text(el.status, 'Čekamo potvrdu dodavanja.');
      el.decline.textContent = 'Otvori korpu';
      return false;
    }

    async function accept() {
      if (!active || adding || navigating || quoting || rollbackPending) return;
      setAdding(true);
      message('');
      if (ambiguousAttempt) {
        try { await verifyAmbiguous(); } finally { if (active) setAdding(false); }
        return;
      }
      let selected = availableVariants(active.offer).find((variant) => String(variant.id) === el.variant.value);
      let freshQuote;
      if (!selected) { setAdding(false); return; }
      try {
        const fresh = await readOffers();
        const offer = chooseOffer(fresh);
        // Recheck the source, product, inventory, and price at the moment of acceptance.
        if (!offer || String(offer.productId) !== String(active.offer.productId) ||
          String(offer.sourceProductId) !== String(active.offer.sourceProductId)) {
          decisions.add(active.data.cartSignature);
          setAdding(false);
          resume(active.intent);
          return;
        }
        const variant = availableVariants(offer).find((item) => String(item.id) === String(selected.id));
        if (!variant || Number(variant.price) !== Number(selected.price)) {
          render(offer, fresh, String(selected.id));
          await refreshQuote();
          message(variant ? 'Cijena je ažurirana. Provjerite ponudu i potvrdite dodavanje.' : 'Odabrana varijanta više nije dostupna. Izaberite drugu ili nastavite bez ponude.');
          setAdding(false);
          return;
        }
        active.data = fresh;
        selected = variant;
        freshQuote = await quoteOffer(offer, variant, await readCart());
        if (!quoted || freshQuote.price !== quoted.price || freshQuote.totalPrice !== quoted.totalPrice) {
          active.offer = offer;
          await refreshQuote();
          message('Cijena ili sadržaj korpe je ažuriran. Provjerite ponudu i potvrdite dodavanje.');
          setAdding(false);
          return;
        }
        active.offer = offer;
      } catch (_) {
        message('Ne možemo provjeriti dostupnost. Pokušajte ponovo ili nastavite bez ponude.');
        setAdding(false);
        return;
      }
      const attempt = active.attempt;
      text(el.status, 'Dodajemo proizvod u korpu…');
      el.accept.textContent = 'Dodajemo…';
      pendingQuote = freshQuote;
      try {
        const response = await request('cart/add.js', {
          method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: selected.id, quantity: 1, properties: { [MARKER]: attempt } }] })
        }, 15000);
        if (!response.ok) {
          if ([400, 404, 422].includes(response.status)) {
            let detail;
            try { detail = await response.json(); } catch (_) { /* use local message */ }
            message(detail && typeof detail.description === 'string' ? detail.description :
              'Proizvod trenutno nije moguće dodati. Pokušajte ponovo ili nastavite bez ponude.');
            text(el.status, '');
            setAdding(false);
            return;
          }
          throw new Error('Uncertain add response');
        }
        await response.json();
        await finalizeDiscount(attempt, freshQuote);
      } catch (_) {
        ambiguousAttempt = attempt;
        await verifyAmbiguous();
        if (active) setAdding(false);
      }
    }

    function onSubmit(event) {
      const form = event.target;
      if (form === bypassForm) return;
      const submitter = event.submitter;
      if (!form || form.tagName !== 'FORM' || !submitter || submitter.name !== 'checkout' ||
        submitter.closest(SKIP_SELECTOR) || !isCartURL(form.action, win.location.origin)) return;
      if (!valid(form, false)) return; // The existing cart-terms handler retains its own feedback.
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!busy) start(snapshot(form, submitter));
    }

    function onClick(event) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest && event.target.closest('a[href]');
      if (!link || link.closest(SKIP_SELECTOR) || link.hasAttribute('download') ||
        (link.target && link.target !== '_self') || !isCheckoutURL(link.href, win.location.origin)) return;
      const form = associatedForm(link);
      if (!valid(form, true)) { event.preventDefault(); return; }
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!busy) start(snapshot(form, null, link.href));
    }

    el.accept.addEventListener('click', accept);
    el.decline.addEventListener('click', decline);
    if (el.close) el.close.addEventListener('click', decline);
    el.variant.addEventListener('change', () => { renderVariant(); message(''); refreshQuote(); });
    dialog.addEventListener('cancel', (event) => { event.preventDefault(); decline(); });
    doc.addEventListener('submit', onSubmit, true);
    doc.addEventListener('click', onClick, true);
    win.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      generation += 1;
      if (dialog.open) dialog.close();
      active = null;
      busy = false;
      adding = false;
      navigating = false;
      setAdding(false);
    });
    return { accept, decline, onSubmit, onClick };
  }

  // Exports are used only by Node's built-in regression tests; the storefront stays an IIFE.
  if (typeof module !== 'undefined' && module.exports) module.exports = {
    availableVariants, chooseOffer, isCheckoutURL, isCartURL, hasAttempt, decisionStore,
    collectCartFields, quantityUpdates, cartCodes, quoteItems, validateQuote, createController
  };
  if (global.document) createController(global, global.document);
}(typeof window !== 'undefined' ? window : globalThis));
